module Sm.UI.InputParser

open System
open System.Text.RegularExpressions
open Fable.Core

open Sm.Core
open Sm.Core.Text
open Sm.Core.Date

open Sm.Model
open Sm.Model.Record

open Sm.UI.Entry

type Arg =
    | Dimension of int * string
    | Ident of string
    | Number of int
    | Unrecognised of string

type SyntaxNode =
    | Link of string
    | Tag of string
    | Text of string
    | Timer of int
    | Directive of Arg list
    | ResourceAttribute of string * string

let private (|Newline|_|) = function
    | "\r" | "\n" | "\r\n" -> Some ()
    | _ -> None

let rec normalise = function
    | [Text t] when String.IsNullOrWhiteSpace(t) -> []
    | Text a :: Text b :: rest -> normalise (Text (a + b) :: rest)
    | Text Newline :: (ResourceAttribute _ as r) :: rest ->  r :: normalise rest
    | node :: rest -> node :: normalise rest
    | [] -> []

let parse =
    let choice (patterns: string array) =
        patterns
        |> join "|"
        |> fun p -> Fable.Core.JS.Constructors.RegExp.Create(p, "gm")

    let args =
        choice [|
            @"(?<ident>[a-zA-Z][a-zA-Z-0-9]*)"
            @"(?<dim_value>-?\d+)(?<dim_unit>[a-zA-Z]+)"
            @"(?<number>-?\d+)"
            @"(?<unrecognised>[^ a-zA-Z-0-9].*?)(?:\s|$)"
        |]

    let nodes =
        choice [|
            @"(^\s*:(?<resource_name>[^ ]+)\s+(?<resource_value>.+)$)"
            @"\b(?<link>(?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}[^\s]*\/?)(?:\b|\B)"
            @"(#(?<tag>\w+))"
            @"(\.\.(?<timer>\d+)(?:$|(?=\s*--)))"
            @"(--\s*(?<directive>(?:-[^-]|[^-])+))"
        |]

    let inline (|Group|_|) (name: string) (m: Match) : string option =
        JsInterop.emitJsExpr (m, name) "$0.groups[$1]"

    let (|Groups|_|) (names: string list) (m: Match) =
        names 
        |> List.map (fun x -> (|Group|_|) x m)
        |> Option.sequence

    function
    | "" -> []
    | text ->
        seq {
            let mutable i = 0
            for m in nodes.Matches(text) do
                if m.Index > i then
                    Text (text[i..m.Index - 1])

                match m with
                | Group "link" url -> Link url
                | Groups ["resource_name"; "resource_value"] [name; value] -> ResourceAttribute (name, value)
                | Group "tag" t -> Tag t
                | Group "timer" (Int m) -> Timer m
                | Group "directive" s ->
                    args.Matches(s)
                    |> Seq.map (function
                        | Groups ["dim_value"; "dim_unit"] [Int value; unit] -> Dimension (value, unit)
                        | Group "number" (Int n) -> Number n
                        | Group "ident" w -> Ident w
                        | Group "unrecognised" s -> Unrecognised s
                        | x -> Unrecognised x.Value
                    )
                    |> List.ofSeq
                    |> function
                        | [] -> Text m.Value
                        | args -> Directive args
                | x -> Text x.Value

                i <- m.Index + m.Length

            if i < text.Length then
                Text (text[i..])
        }
        |> List.ofSeq
        |> normalise

let formatArg = function
    | Dimension (value, unit) -> sprintf "%i%s" value unit
    | Ident w -> w
    | Number n -> sprintf "%i" n
    | Unrecognised x -> x

let formatArgs args =
    ("", args)
    ||> List.fold (fun s arg -> 
        let s = if s = "" then s else s + " "
        s + formatArg arg
    )

let format nodes =
    ("", nodes)
    ||> List.fold (fun s -> function
        | Link url -> s + url
        | Tag t -> s + "#" + t
        | Text t -> s + t
        | Timer e -> s + ".." + string e
        | Directive args -> s + "-- " + (formatArgs args)
        | ResourceAttribute (name, value) ->
            let nl = if s.Length > 0 then "\n" else ""
            s + nl + ":" + name + " " + value
    )

type Input = {
    Tokens: SyntaxNode list
    Text: string
    Metadata: Map<string, string>
}

module Directive =
    [<AutoOpen>]
    module private Match =
        let (|Any|_|) expected actual =
            let m = equalsIgnoreCase actual
            if expected |> List.exists m then Some ()
            else None

        let (|NumberOrDimension|_|) expected arg =
            match arg with
            | [Number x; Ident (Any expected)] -> Some x
            | [Dimension (x, Any expected)] -> Some x
            | _ -> None

    let evaluate = function
        | Ident (Any ["due"; "by"; "in"]) :: rest ->
            let addDays x = Some (DateOnly.Today.AddDays(x))
            let addMonths x = Some (DateOnly.Today.AddMonths(x))

            match rest with
            | [Ident (Any ["today"; "now"])] -> addDays 0
            | [Ident (Any ["tomorrow"; "to-morrow"; "tm"])] -> addDays 1
            | NumberOrDimension ["d"; "day"; "days"] x -> addDays x
            | NumberOrDimension ["w"; "wk"; "week"; "weeks"] x -> addDays (x * 7)
            | NumberOrDimension ["m"; "mo"; "month"; "months"] x -> addMonths x
            | _ -> None
            |> function
                | Some date ->
                    let msg = Accessor.dueDate.Set(date)
                    Ok msg
                | None -> Error (sprintf "Unable to recognise a date for '%s'" (formatArgs rest))

        | x :: _ -> Error (sprintf "Unrecognised directive '%s'" (formatArg x))
        | [] -> Error "Directive has no arguments"

let evaluate nodes =
    match nodes with
    | [] -> Error "Text must not be empty"
    | nodes ->
        let directives, tokens =
            nodes
            |> List.partition (function Directive _ -> true | _ -> false)

        directives
        |> List.map (function Directive terms -> terms | _ -> [])
        |> Result.traverse Directive.evaluate
        |> Result.map (fun messages ->
            {
                Tokens = tokens
                Text = format tokens
                Metadata = Meta.collect messages
            }
        )

module InputEditor =
    let validate = function
        | None -> Ok ()
        | Some text ->
            parse text
            |> evaluate
            |> Result.map (fun _ -> ())

    let validateNonEmpty = function
        | None | Some EmptyOrWhitespace -> Error "Text must not be empty"
        | x -> validate x

    let accept f text =
        parse text
        |> evaluate
        |> Result.iter f

    let apply (record: Record.Type) input =
        seq {
            SetText input.Text
            for name, value in input.Metadata |> Map.toSeq do
                SetMetadata (name, value)
        }
        |> Seq.iter record.Post
